import React, { useState, useEffect } from "react";
import { useDeviceDetection } from "../../hooks/useDeviceDetection";
import styles from "../metamask-error-recovery/MetaMaskErrorRecovery.module.scss";

interface PhantomErrorRecoveryProps {
  onClose?: () => void;
  autoShow?: boolean;
}

const PhantomErrorRecovery: React.FC<PhantomErrorRecoveryProps> = ({
  onClose,
  autoShow = false,
}) => {
  const [isVisible, setIsVisible] = useState(autoShow);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { walletType } = useDeviceDetection();

  // Auto-show when Phantom connection errors occur
  useEffect(() => {
    const handlePhantomError = (event: any) => {
      if (event.detail?.error) {
        setIsVisible(true);
      }
    };

    window.addEventListener("phantom-error", handlePhantomError);
    return () =>
      window.removeEventListener("phantom-error", handlePhantomError);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  const handleRefreshPage = () => {
    setIsRefreshing(true);
    window.location.reload();
  };

  const handleDisablePhantom = () => {
    alert(
      "To disable Phantom wallet:\n\n" +
        "1. Go to chrome://extensions/\n" +
        '2. Find "Phantom" extension\n' +
        "3. Toggle it OFF\n" +
        "4. Refresh this page\n\n" +
        "This will allow your other wallets (like Rabby) to work properly."
    );
  };

  const handleRetryConnection = async () => {
    // Clear any pending Phantom connections
    if ((window as any).phantom?.ethereum) {
      try {
        await (window as any).phantom.ethereum.request({
          method: "wallet_requestPermissions",
          params: [{ eth_accounts: {} }],
        });
        handleClose();
      } catch (error) {
        console.error("Phantom retry failed:", error);
      }
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h3>Phantom Wallet Conflict</h3>
          <button
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.errorIcon}>👻</div>

          <div className={styles.message}>
            <p>
              Phantom wallet is interfering with your connection attempts. This
              is a common issue when multiple wallet extensions are installed.
            </p>

            <div className={styles.explanation}>
              <p>
                <strong>What's happening:</strong>
              </p>
              <ul>
                <li>Phantom wallet is trying to become the default wallet</li>
                <li>It may automatically popup without your action</li>
                <li>
                  This prevents other wallets (like Rabby) from connecting
                </li>
                <li>Multiple wallet extensions are competing for control</li>
              </ul>
            </div>
          </div>

          <div className={styles.actions}>
            <button
              className={`${styles.button} ${styles.primary}`}
              onClick={handleDisablePhantom}
            >
              Show How to Disable Phantom
            </button>

            <button
              className={`${styles.button} ${styles.secondary}`}
              onClick={handleRetryConnection}
              disabled={walletType !== "generic"}
            >
              Retry Connection
            </button>

            <button
              className={`${styles.button} ${styles.secondary}`}
              onClick={handleRefreshPage}
              disabled={isRefreshing}
            >
              {isRefreshing ? "Refreshing..." : "Refresh Page"}
            </button>
          </div>

          <div className={styles.tips}>
            <h4>Solutions:</h4>
            <ul>
              <li>
                <strong>Temporary fix:</strong> Disable Phantom extension
                temporarily
              </li>
              <li>
                <strong>Use different browsers:</strong> Phantom in Firefox,
                Rabby in Chrome
              </li>
              <li>
                <strong>Incognito mode:</strong> Enable only your preferred
                wallet
              </li>
              <li>
                <strong>Phantom for Solana only:</strong> Keep it for Solana,
                use others for Ethereum
              </li>
            </ul>

            <div
              style={{
                marginTop: "1rem",
                padding: "0.5rem",
                background: "#f0f0f0",
                borderRadius: "4px",
                fontSize: "0.9rem",
              }}
            >
              <strong>Pro tip:</strong> Phantom is excellent for Solana but can
              conflict with Ethereum wallets. Consider using it only for Solana
              transactions.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhantomErrorRecovery;
